import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { bookingService } from '@/lib/api/booking.service';
import { handleApiError } from '@/lib/api/client';
import type { Booking, BookingLineItem, BookingLineItemStatus, BookingStatus } from '@/types/booking.types';

const BOOKING_STATUS_BADGE: Record<BookingStatus, { label: string; className: string }> = {
  pending:     { label: 'Pending',     className: 'bg-amber-100 text-amber-800' },
  confirmed:   { label: 'Confirmed',   className: 'bg-blue-100 text-blue-800' },
  in_progress: { label: 'In Progress', className: 'bg-indigo-100 text-indigo-800' },
  completed:   { label: 'Completed',   className: 'bg-green-100 text-green-800' },
  cancelled:   { label: 'Cancelled',   className: 'bg-red-100 text-red-800' },
  disputed:    { label: 'Disputed',    className: 'bg-orange-100 text-orange-800' },
};

const LINE_ITEM_STATUS_BADGE: Record<BookingLineItemStatus, { label: string; className: string }> = {
  planned:     { label: 'Planned',     className: 'bg-gray-100 text-gray-800' },
  in_progress: { label: 'In Progress', className: 'bg-indigo-100 text-indigo-800' },
  completed:   { label: 'Completed',   className: 'bg-green-100 text-green-800' },
  cancelled:   { label: 'Cancelled',   className: 'bg-red-100 text-red-800' },
};

const formatCurrency = (amount: number, currency: string) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

const formatDate = (dateStr: string | null) =>
  dateStr ? new Date(dateStr).toLocaleDateString() : '—';

export const BookingLineItemsPage: React.FC = () => {
  const { celebrationId, bookingId } = useParams<{ celebrationId: string; bookingId: string }>();
  const { t } = useTranslation(['bookings', 'common']);

  const celebrationIdNum = useMemo(() => Number.parseInt(celebrationId ?? '', 10), [celebrationId]);
  const bookingIdNum = useMemo(() => Number.parseInt(bookingId ?? '', 10), [bookingId]);

  const [booking, setBooking] = useState<Booking | null>(null);
  const [lineItems, setLineItems] = useState<BookingLineItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transitioning, setTransitioning] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!Number.isFinite(celebrationIdNum) || !Number.isFinite(bookingIdNum)) return;
    setLoading(true);
    setError(null);
    try {
      const [bk, items] = await Promise.all([
        bookingService.getBooking(celebrationIdNum, bookingIdNum),
        bookingService.listLineItems(celebrationIdNum, bookingIdNum),
      ]);
      setBooking(bk);
      setLineItems(items);
    } catch (err: unknown) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, [celebrationIdNum, bookingIdNum]);

  useEffect(() => {
    load();
  }, [load]);

  const handleLineItemTransition = async (lineItemId: number, status: string) => {
    setTransitioning(lineItemId);
    setError(null);
    try {
      const updated = await bookingService.transitionLineItemStatus(
        celebrationIdNum,
        bookingIdNum,
        lineItemId,
        status
      );
      setLineItems((prev) => prev.map((li) => (li.id === lineItemId ? updated : li)));
    } catch (err: unknown) {
      setError(handleApiError(err));
    } finally {
      setTransitioning(null);
    }
  };

  const lineItemsTotal = lineItems.reduce((sum, li) => sum + li.line_amount, 0);
  const currency = booking?.currency ?? 'USD';
  const reconciledTotal = lineItemsTotal + (booking?.adjustment_amount ?? 0);

  return (
    <>
      <AppBreadcrumbs />
      <PageContainer width="wide">
        <div className="mb-4">
          <Link
            to={`/celebrations/${celebrationIdNum}/bookings`}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" />
            {t('bookings:lineItemsPage.backToBookings')}
          </Link>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
          </div>
        )}

        {error && (
          <div className="rounded-md bg-destructive/10 text-destructive px-4 py-2 text-sm mb-4">
            {error}
          </div>
        )}

        {!loading && booking && (
          <>
            {/* Booking summary header */}
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="text-base">
                  Booking {booking.booking_number}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">{t('bookings:lineItemsPage.fields.status')}</p>
                    {(() => {
                      const badge = BOOKING_STATUS_BADGE[booking.status] ?? { label: booking.status, className: 'bg-gray-100 text-gray-800' };
                      return (
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}>
                          {badge.label}
                        </span>
                      );
                    })()}
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t('bookings:lineItemsPage.fields.eventDate')}</p>
                    <p className="font-medium">{formatDate(booking.event_date)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t('bookings:lineItemsPage.fields.totalAmount')}</p>
                    <p className="font-medium">{formatCurrency(booking.total_amount, currency)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t('bookings:lineItemsPage.fields.adjustment')}</p>
                    <p className="font-medium">{formatCurrency(booking.adjustment_amount, currency)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Line items table */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('bookings:lineItems')}</CardTitle>
              </CardHeader>
              <CardContent>
                {lineItems.length === 0 ? (
                  <p className="text-muted-foreground text-sm py-4">{t('bookings:lineItemsPage.table.empty')}</p>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-left text-muted-foreground">
                            <th className="pb-2 pr-3 font-medium">{t('bookings:lineItemsPage.table.description')}</th>
                            <th className="pb-2 pr-3 font-medium">{t('bookings:lineItemsPage.table.scheduledDate')}</th>
                            <th className="pb-2 pr-3 font-medium">{t('bookings:lineItemsPage.table.time')}</th>
                            <th className="pb-2 pr-3 font-medium">{t('bookings:lineItemsPage.table.amount')}</th>
                            <th className="pb-2 pr-3 font-medium">{t('bookings:lineItemsPage.table.status')}</th>
                            <th className="pb-2 font-medium">{t('bookings:lineItemsPage.table.actions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lineItems.map((li) => {
                            const badge = LINE_ITEM_STATUS_BADGE[li.status] ?? { label: li.status, className: 'bg-gray-100 text-gray-800' };
                            const isBusy = transitioning === li.id;
                            const timeRange =
                              li.scheduled_start_time && li.scheduled_end_time
                                ? `${li.scheduled_start_time}–${li.scheduled_end_time}`
                                : li.scheduled_start_time ?? '—';
                            return (
                              <tr key={li.id} className="border-b last:border-0 hover:bg-muted/30">
                                <td className="py-2 pr-3">{li.description ?? t('bookings:lineItemsPage.table.defaultDescription')}</td>
                                <td className="py-2 pr-3">{formatDate(li.scheduled_date)}</td>
                                <td className="py-2 pr-3">{timeRange}</td>
                                <td className="py-2 pr-3">{formatCurrency(li.line_amount, currency)}</td>
                                <td className="py-2 pr-3">
                                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}>
                                    {badge.label}
                                  </span>
                                </td>
                                <td className="py-2">
                                  {li.status === 'planned' && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      disabled={isBusy}
                                      onClick={() => handleLineItemTransition(li.id, 'in_progress')}
                                    >
                                    {isBusy ? t('bookings:lineItemsPage.actions.updating') : t('bookings:lineItemsPage.actions.markInProgress')}
                                    </Button>
                                  )}
                                  {li.status === 'in_progress' && (
                                    <Button
                                      size="sm"
                                      disabled={isBusy}
                                      onClick={() => handleLineItemTransition(li.id, 'completed')}
                                    >
                                    {isBusy ? t('bookings:lineItemsPage.actions.updating') : t('bookings:lineItemsPage.actions.markCompleted')}
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Total reconciliation */}
                    <div className="mt-4 border-t pt-4 text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('bookings:lineItemsPage.reconciliation.subtotal')}</span>
                        <span>{formatCurrency(lineItemsTotal, currency)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('bookings:lineItemsPage.reconciliation.adjustment')}</span>
                        <span>{formatCurrency(booking.adjustment_amount, currency)}</span>
                      </div>
                      <div className="flex justify-between font-medium border-t pt-1 mt-1">
                        <span>{t('bookings:lineItemsPage.reconciliation.total')}</span>
                        <span className={reconciledTotal === booking.total_amount ? 'text-green-700' : 'text-amber-700'}>
                          {formatCurrency(reconciledTotal, currency)}
                          {reconciledTotal !== booking.total_amount && (
                            <span className="ml-2 text-xs font-normal">{t('bookings:lineItemsPage.reconciliation.bookingTotal', { amount: formatCurrency(booking.total_amount, currency) })}</span>
                          )}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </PageContainer>
    </>
  );
};
